import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import {
  type ChartSeriesSource,
  SERIES_MIN_TIMEFRAME_SECONDS,
  type SeriesFetchContext,
} from '@/lib/tradingview/datafeeds/series-sources'
import { fetchChartData } from '@/service/chart.api'
import {
  buildCrossExchangeRequest,
  CROSS_EXCHANGE_DERIVATIVE_VENUES,
  CROSS_EXCHANGE_FAMILIES,
  CROSS_EXCHANGE_OI_SCOPES,
  CROSS_EXCHANGE_VOLUME_SCOPES,
  CROSS_EXCHANGE_VOLUME_VENUES,
  crossExchangeCvdPoints,
  crossExchangeFundingPoints,
  crossExchangeOpenInterestPoints,
  type CrossExchangeScopeInput,
  type CrossExchangeVenue,
  type CvdMeasure,
  type FundingMeasure,
  type OpenInterestMeasure,
  selectedExchanges,
  selectedInstrumentTypes,
} from '@/service/cross-exchange-chart.types'
import {
  type CandleFamily,
  type CandleList,
  CrossExchangeInstrumentType,
} from '@/types/generated/public_api'

/**
 * The three "(All exchanges)" studies, as own-fetch series sources.
 *
 * They can't ride the shared candle fetch: that request names one instrument
 * on one venue, and these name an ASSET across all of them. Each study issues
 * its own `chartData` call with `crossExchange` set, which the API answers by
 * resolving the asset to its legs and folding them.
 *
 * One source per study rather than one source for all three, because the
 * subscription key is `(sourceId, symbol, exchange, timeframe, inputs)` — a
 * shared source would make each study's own venue toggles fork the key anyway,
 * while forcing every fetch to carry families the other two studies wanted.
 */

/**
 * Quote currencies that venues concatenate onto the base with no separator
 * (`BTCUSDT`, `ETHBTC`). Longest first so `BTCUSDC` doesn't strip to `BTCUSD`.
 * The shared parser handles the separated forms — Deribit's `BTC-PERPETUAL`
 * and `BTC_USDC-…`, Upbit's `KRW-BTC` — but has no rule for these.
 */
const CONCATENATED_QUOTES = [
  'FDUSD',
  'USDC',
  'USDT',
  'USDE',
  'BUSD',
  'USD',
  'DAI',
  'KRW',
  'EUR',
  'TRY',
  'BTC',
  'ETH',
]

/**
 * The chart's symbol is an instrument (`BTC-PERPETUAL`, `BTCUSDT`); the API
 * wants the asset it settles in. Instrument-map-free on purpose — a series
 * fetch context carries only the symbol string, so this is a symbology rule,
 * not a lookup.
 *
 * The limit that follows from that: an ETF ticker (`IBIT`) names its
 * underlying nowhere in the string, so a chart on one can't derive `BTC`. ETFs
 * still join the aggregate as legs — they just can't be the chart you open it
 * from.
 */
export function assetForContext(ctx: SeriesFetchContext): string {
  const parsed = resolveBaseCurrency([], ctx.symbol)
  const symbol = ctx.symbol.toUpperCase()
  // The parser fell through to "the whole thing is the base" — which is right
  // for `BTC` and wrong for `BTCUSDT`.
  if (parsed !== symbol) return parsed
  for (const quote of CONCATENATED_QUOTES) {
    if (symbol.length > quote.length && symbol.endsWith(quote)) {
      return symbol.slice(0, -quote.length)
    }
  }
  return parsed
}

/** Read a select input, falling back to `fallback` for the empty context TV
 *  probes `main` with before any study settings exist. */
function selectValue<T extends string>(
  ctx: SeriesFetchContext,
  inputId: string,
  allowed: readonly T[],
  fallback: T
): T {
  const raw = ctx.inputs?.[inputId]
  return allowed.includes(raw as T) ? (raw as T) : fallback
}

async function fetchAggregate(
  ctx: SeriesFetchContext,
  family: CandleFamily,
  venues: CrossExchangeVenue[],
  instrumentTypes: CrossExchangeInstrumentType[],
  signal: AbortSignal
): Promise<CandleList | null> {
  const request = buildCrossExchangeRequest({
    asset: assetForContext(ctx),
    timeframeSeconds: ctx.timeframeSeconds,
    fromSec: ctx.fromSec,
    toSec: ctx.toSec,
    family,
    exchanges: selectedExchanges(ctx.inputs, venues),
    instrumentTypes,
  })
  const data = await fetchChartData(request, signal)
  return data.candles ?? null
}

/** One checkbox per venue, all on by default — the study opens as a true
 *  "all exchanges" aggregate and narrows from there. */
function venueInputs(venues: CrossExchangeVenue[]) {
  return venues.map((venue) => ({
    id: venue.inputId,
    name: venue.label,
    type: 'bool' as const,
    defval: true,
  }))
}

function scopeInputs(scopes: CrossExchangeScopeInput[]) {
  return scopes.map((scope) => ({
    id: scope.inputId,
    name: scope.label,
    type: 'bool' as const,
    defval: true,
  }))
}

/** Study-input specs, exported so the analytics definitions and the sources
 *  can't drift apart on input ids. */
export const CROSS_EXCHANGE_OI_INPUTS = [
  {
    id: 'measure',
    name: 'Measure',
    type: 'select' as const,
    options: ['base', 'quote'],
    optionsTitles: { base: 'Base', quote: 'Quote (USD)' },
    defval: 'quote',
  },
  ...scopeInputs(CROSS_EXCHANGE_OI_SCOPES),
  ...venueInputs(CROSS_EXCHANGE_DERIVATIVE_VENUES),
]

export const CROSS_EXCHANGE_FUNDING_INPUTS = [
  {
    id: 'measure',
    name: 'Measure',
    type: 'select' as const,
    options: ['rate', 'apr'],
    optionsTitles: { rate: 'Per interval', apr: 'Annualized (APR)' },
    defval: 'rate',
  },
  ...venueInputs(CROSS_EXCHANGE_DERIVATIVE_VENUES),
]

export const CROSS_EXCHANGE_CVD_INPUTS = [
  {
    id: 'measure',
    name: 'Measure',
    type: 'select' as const,
    options: ['base', 'quote'],
    optionsTitles: { base: 'Base', quote: 'Quote (USD)' },
    defval: 'base',
  },
  ...scopeInputs(CROSS_EXCHANGE_VOLUME_SCOPES),
  ...venueInputs(CROSS_EXCHANGE_VOLUME_VENUES),
]

export const crossExchangeOpenInterestSource: ChartSeriesSource = {
  id: 'crossExchangeOpenInterest',
  metrics: ['crossExchangeOpenInterest'],
  refresh: 'bar-close',
  // The API refuses sub-minute cross-exchange windows: a SAMPLE BY over the
  // raw trades tables across every leg is the shape that has taken a pooled
  // QuestDB connection down on a single instrument.
  minTimeframeSeconds: SERIES_MIN_TIMEFRAME_SECONDS,
  // Open interest is a stock. A bucket in which no leg reported still has a
  // level, so hold the last one rather than breaking the line.
  carryForward: true,
  fetchHistory: async (ctx, signal) => {
    const candles = await fetchAggregate(
      ctx,
      CROSS_EXCHANGE_FAMILIES.openInterest,
      CROSS_EXCHANGE_DERIVATIVE_VENUES,
      selectedInstrumentTypes(ctx.inputs, CROSS_EXCHANGE_OI_SCOPES),
      signal
    )
    if (!candles) return []
    const measure = selectValue<OpenInterestMeasure>(ctx, 'measure', ['base', 'quote'], 'quote')
    return crossExchangeOpenInterestPoints(candles, 'crossExchangeOpenInterest', measure)
  },
}

export const crossExchangeFundingRateSource: ChartSeriesSource = {
  id: 'crossExchangeFundingRate',
  metrics: ['crossExchangeFundingRate'],
  refresh: 'bar-close',
  minTimeframeSeconds: SERIES_MIN_TIMEFRAME_SECONDS,
  // Venues publish funding on their own cadence (8h on most, 1h on some), so
  // the weighted rate holds between prints rather than dropping to nothing.
  carryForward: true,
  fetchHistory: async (ctx, signal) => {
    const candles = await fetchAggregate(
      ctx,
      CROSS_EXCHANGE_FAMILIES.funding,
      CROSS_EXCHANGE_DERIVATIVE_VENUES,
      // Perpetuals only, and not a user choice: a dated future has no funding
      // rate, so widening the scope would only enlarge the leg read.
      [CrossExchangeInstrumentType.CROSS_EXCHANGE_INSTRUMENT_TYPE_PERPETUAL],
      signal
    )
    if (!candles) return []
    const measure = selectValue<FundingMeasure>(ctx, 'measure', ['rate', 'apr'], 'rate')
    return crossExchangeFundingPoints(candles, 'crossExchangeFundingRate', measure)
  },
}

export const crossExchangeCvdSource: ChartSeriesSource = {
  id: 'crossExchangeCvd',
  metrics: ['crossExchangeCvd'],
  refresh: 'bar-close',
  minTimeframeSeconds: SERIES_MIN_TIMEFRAME_SECONDS,
  // The per-bucket delta is a flow, but the plotted value is its running sum:
  // a bucket with no trades leaves the cumulative line flat, not absent.
  carryForward: true,
  fetchHistory: async (ctx, signal) => {
    const candles = await fetchAggregate(
      ctx,
      CROSS_EXCHANGE_FAMILIES.cvd,
      CROSS_EXCHANGE_VOLUME_VENUES,
      selectedInstrumentTypes(ctx.inputs, CROSS_EXCHANGE_VOLUME_SCOPES),
      signal
    )
    if (!candles) return []
    const measure = selectValue<CvdMeasure>(ctx, 'measure', ['base', 'quote'], 'base')
    return crossExchangeCvdPoints(candles, 'crossExchangeCvd', measure)
  },
}

export const CROSS_EXCHANGE_SOURCES: ChartSeriesSource[] = [
  crossExchangeOpenInterestSource,
  crossExchangeFundingRateSource,
  crossExchangeCvdSource,
]
